import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import axios from 'axios';

interface User {
  _id?: string;
  id?: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  isActive: boolean;
  company?: { id: string; name: string; slug: string } | null;
  createdAt?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  setUserFromProfile: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const ADMIN_APP_ROLES = ['admin', 'coach', 'entreprise'];

const isAdminAppRole = (role?: string) => Boolean(role && ADMIN_APP_ROLES.includes(role));
const TOKEN_STORAGE_KEY = 'admin_token';
const USER_STORAGE_KEY = 'admin_user';

// API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Configure axios defaults
axios.defaults.baseURL = API_BASE_URL;
axios.defaults.timeout = 10000; // 10 secondes timeout
// ngrok free plan displays a browser warning/splash page unless this header is present.
// This header is safe and helps avoid non-JSON HTML responses that break CORS handling.
axios.defaults.headers.common['ngrok-skip-browser-warning'] = '1';

// Intercepteur pour gérer les erreurs réseau
// Ensures ngrok free plan doesn't inject an HTML splash page.
axios.interceptors.request.use(
  (config) => {
    config.headers = config.headers ?? {};
    (config.headers as any)['ngrok-skip-browser-warning'] = '1';
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      console.error('⏰ Timeout de requête:', error.config?.url);
    } else if (error.code === 'ERR_NETWORK' || !error.response) {
      console.error('🌐 Erreur réseau:', error.config?.url);
    } else if (error.response?.status === 401) {
      const requestUrl = String(error.config?.url || '');
      // Auth credential failures (wrong password, bad login) are 401s that must not force logout
      const isCredentialEndpoint =
        requestUrl.includes('/auth/change-password') ||
        requestUrl.includes('/auth/login') ||
        requestUrl.includes('/auth/forgot-password') ||
        requestUrl.includes('/auth/reset-password');

      if (!isCredentialEndpoint) {
        console.error('🔐 Token expiré ou invalide');
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Global fetch patch (admin pages use `fetch`).
// Must run at module-load time to avoid race conditions with page effects.
if (typeof window !== 'undefined') {
  const w = window as any;
  if (!w.__ngrokFetchPatched) {
    w.__ngrokFetchPatched = true;
    const originalFetch = window.fetch.bind(window);
    window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
      const tokenFromStorage = localStorage.getItem(TOKEN_STORAGE_KEY);
      const headers = new Headers(init?.headers as any);

      if (tokenFromStorage && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${tokenFromStorage}`);
      }

      headers.set('ngrok-skip-browser-warning', '1');
      return originalFetch(input, { ...init, headers });
    };
  }
}

const extractApiErrorMessage = (error: any, fallback: string): string => {
  const payload = error?.response?.data;

  if (typeof payload?.error === 'string' && payload.error.trim()) {
    return payload.error;
  }

  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return payload.message;
  }

  if (typeof error?.message === 'string' && error.message.trim() && error.message !== '[object Object]') {
    return error.message;
  }

  return fallback;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const raw = localStorage.getItem(USER_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_STORAGE_KEY));
  const [loading, setLoading] = useState(true);

  // Set token in axios headers when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  }, [token]);

  // Rehydrate token if state is empty but localStorage still has it
  useEffect(() => {
    if (!token) {
      const storedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
      if (storedToken) {
        setToken(storedToken);
      }
    }
  }, [token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      const activeToken = token || localStorage.getItem(TOKEN_STORAGE_KEY);
      if (activeToken) {
        try {
          const response = await axios.get('/auth/profile', {
            headers: { Authorization: `Bearer ${activeToken}` }
          });
          const userData = response.data.user;
          
          // Only allow admin and coach roles
          if (isAdminAppRole(userData.role)) {
            setUser(userData);
            localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
            console.log('👤 Utilisateur chargé:', userData.email, '- Rôle:', userData.role);
          } else {
            // Invalid role, clear token
            console.log('⚠️ Rôle invalide:', userData.role);
            setToken(null);
            setUser(null);
            localStorage.removeItem(USER_STORAGE_KEY);
          }
        } catch (error: any) {
          console.error('Auth check failed:', error);
          
          // Si c'est une erreur 401, le token est expiré
          if (error.response?.status === 401) {
            console.log('🔐 Token expiré, déconnexion automatique');
            setToken(null);
            setUser(null);
            localStorage.removeItem(USER_STORAGE_KEY);
          } else if (error.code === 'ERR_NETWORK' || !error.response) {
            console.log('🌐 Erreur réseau, tentative de reconnexion...');
            // Keep cached user to avoid forcing logout on transient network/CORS issues
            const cachedUserRaw = localStorage.getItem(USER_STORAGE_KEY);
            if (!user && cachedUserRaw) {
              try {
                setUser(JSON.parse(cachedUserRaw));
              } catch {
                // ignore parse error
              }
            }
          } else {
            // Autres erreurs, déconnecter
            setToken(null);
            setUser(null);
            localStorage.removeItem(USER_STORAGE_KEY);
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const response = await axios.post('/auth/login', { email, password });
      const { user, token: newToken } = response.data;
      
      // Log des informations de connexion
      console.log('✅ Connexion réussie');
      console.log('👤 Utilisateur:', user.email, '- Rôle:', user.role);
      
      // Only allow admin and coach roles
      if (isAdminAppRole(user.role)) {
        // Persist immediately to avoid refresh race conditions
        localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        setUser(user);
        setToken(newToken);
      } else {
        throw new Error('Access denied. Admin access required.');
      }
    } catch (error: any) {
      throw new Error(extractApiErrorMessage(error, 'Unable to sign in. Please check your credentials and try again.'));
    }
  };

  const logout = () => {
    console.log('🚪 Déconnexion de l\'utilisateur');
    setUser(null);
    setToken(null);
    localStorage.removeItem(USER_STORAGE_KEY);
  };

  const checkAuth = async () => {
    if (token) {
      try {
        const response = await axios.get('/auth/profile', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const userData = response.data.user;
        
        // Only allow admin and coach roles
        if (isAdminAppRole(userData.role)) {
          setUser(userData);
          localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
        } else {
          // Invalid role, clear token
          setToken(null);
          setUser(null);
          localStorage.removeItem(USER_STORAGE_KEY);
        }
      } catch (error) {
        // Token is invalid, clear it
        setToken(null);
        setUser(null);
        localStorage.removeItem(USER_STORAGE_KEY);
      }
    }
    setLoading(false);
  };

  const setUserFromProfile = useCallback((userData: User) => {
    setUser(userData);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
  }, []);

  const value = {
    user,
    isLoading: loading,
    login,
    logout,
    checkAuth,
    setUserFromProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 